import { useState, useEffect } from "react";
import axios from "axios";
import { AFF_API, getAffHeaders } from "../../context/AffAuthContext";
import { Bank, ArrowUpRight, CheckCircle, X, Wallet, Receipt, ShieldCheck, IdentificationCard } from "@phosphor-icons/react";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import PanVerifyModal from "./PanVerifyModal";

/**
 * Withdrawal flow (Jun 2026):
 *  STEP 1 — Pick / add a destination (UPI or Bank). Each new destination is
 *           verified via Razorpay Penny Drop (name-match against registered name).
 *  STEP 2 — Once a verified destination is selected, enter amount. Live fee
 *           preview deducts 18% GST + ₹15 admin fee.
 *  STEP 3 — Submit → backend re-validates fees + destination + cap.
 */
export default function WithdrawalForm({ onUpdate, open: controlledOpen, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (v) => {
    if (isControlled) onOpenChange?.(v); else setInternalOpen(v);
  };
  const [stage, setStage] = useState("destination"); // "destination" | "amount"
  const [amount, setAmount] = useState("");
  const [feePreview, setFeePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [registeredName, setRegisteredName] = useState("");
  const [selectedDestId, setSelectedDestId] = useState("");
  const [addMode, setAddMode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState("");

  // New-destination fields
  const [destType, setDestType] = useState("bank");
  const [holderName, setHolderName] = useState("");
  const [newUpi, setNewUpi] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNum, setAccountNum] = useState("");
  const [ifsc, setIfsc] = useState("");

  // PAN state
  const [panState, setPanState] = useState({ verified: false, pan_masked: null, name_on_pan: null });
  const [panModalOpen, setPanModalOpen] = useState(false);

  const refreshState = async () => {
    try {
      const [wb, dests, pan] = await Promise.all([
        axios.get(`${AFF_API}/affiliate/withdrawable`, { headers: getAffHeaders() }),
        axios.get(`${AFF_API}/affiliate/withdraw/destinations`, { headers: getAffHeaders() }),
        axios.get(`${AFF_API}/affiliate/kyc/pan`, { headers: getAffHeaders() }),
      ]);
      setBreakdown(wb.data);
      setDestinations(dests.data.destinations || []);
      setRegisteredName(dests.data.registered_name || "");
      setHolderName((prev) => prev || dests.data.registered_name || "");
      setPanState(pan.data || { verified: false });
      const verified = (dests.data.destinations || []).filter((d) => d.verified);
      const primary = verified.find((d) => d.is_primary) || verified[0];
      if (primary && !selectedDestId) setSelectedDestId(primary.destination_id);
    } catch (err) {
      // Modal-open fetch: keep UI usable even if the destinations refresh
      // fails. Log for debugging; the user can retry by closing/reopening.
      console.debug("[WithdrawalForm] refreshState failed:", err?.response?.status || err?.message);
    }
  };

  useEffect(() => { if (open) refreshState(); }, [open]);

  // Live fee preview (debounced via simple effect)
  useEffect(() => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setFeePreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${AFF_API}/affiliate/withdrawal/preview`, {
          params: { amount: amt },
          headers: getAffHeaders(),
        });
        setFeePreview(res.data);
      } catch { setFeePreview(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [amount]);

  const maxWithdraw = breakdown?.available_to_withdraw ?? 0;

  const handleVerifyDestination = async () => {
    setVerifyError("");
    if (!holderName.trim()) { setVerifyError("Enter the account holder name"); return; }
    if (destType === "upi" && !/^[a-zA-Z0-9._-]{2,256}@[a-zA-Z]{2,64}$/.test(newUpi)) {
      setVerifyError("Enter a valid UPI ID"); return;
    }
    if (destType === "bank" && !(bankName.trim() && accountNum.trim() && ifsc.trim())) {
      setVerifyError("Fill all bank fields"); return;
    }
    setVerifying(true);
    try {
      const payload = destType === "upi"
        ? { type: "upi", upi_id: newUpi, holder_name: holderName }
        : { type: "bank", holder_name: holderName, bank_name: bankName, account_number: accountNum, ifsc_code: ifsc };
      const res = await axios.post(
        `${AFF_API}/affiliate/withdraw/verify-destination`,
        payload,
        { headers: getAffHeaders() },
      );
      if (res.data.verified) {
        toast.success(res.data.stub ? "Verified via Razorpay (dev stub)" : "Verified via Razorpay name-match");
        setAddMode(false);
        setSelectedDestId(res.data.destination_id);
        setNewUpi(""); setBankName(""); setAccountNum(""); setIfsc("");
        await refreshState();
      } else {
        setVerifyError(res.data.error || "Verification failed");
        toast.error(res.data.error || "Verification failed");
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setVerifyError(typeof d === "string" ? d : "Verification failed");
      toast.error(typeof d === "string" ? d : "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e, acknowledgeHigherTds = false) => {
    if (e && e.preventDefault) e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    const minReq = feePreview?.min_withdrawal || 1000;
    if (amt < minReq) { toast.error(`Minimum withdrawal is ₹${minReq.toLocaleString("en-IN")}`); return; }
    if (amt > maxWithdraw) { toast.error(`Maximum withdrawable: ₹${maxWithdraw.toLocaleString("en-IN")}`); return; }
    const dest = destinations.find((d) => d.destination_id === selectedDestId && d.verified);
    if (!dest) { toast.error("Select a verified destination"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${AFF_API}/affiliate/withdrawal`, {
        amount: amt,
        destination_type: dest.type,
        destination_verification_id: dest.destination_id,
        acknowledge_higher_tds: acknowledgeHigherTds,
      }, { headers: getAffHeaders() });
      const auto = res.data.auto_approved;
      toast.success(
        auto
          ? `Auto-approved! ₹${res.data.fees.net_payout.toLocaleString("en-IN")} will reach your account within 1 hour.`
          : `Withdrawal of ₹${res.data.fees.net_payout.toLocaleString("en-IN")} submitted for admin review.`
      );
      setOpen(false); setAmount(""); setFeePreview(null); setStage("destination");
      onUpdate?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Structured PAN threshold error → keep modal open, PAN gate UI already visible
      if (detail && typeof detail === "object" && detail.code === "PAN_REQUIRED_THRESHOLD") {
        toast.error(detail.message || "PAN verification required — monthly total crosses ₹20,000.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Withdrawal failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    if (isControlled) return null; // parent controls visibility; render nothing when closed
    return (
      <button onClick={() => { setOpen(true); setStage("destination"); }} data-testid="open-withdrawal-btn"
        className="px-3 py-1.5 rounded-md text-[10px] font-bold bg-emerald-600/10 text-emerald-500 border border-emerald-600/20 hover:bg-emerald-600/20 transition-colors flex items-center gap-1">
        Withdraw <ArrowUpRight size={10} weight="bold" />
      </button>
    );
  }

  const verifiedDestinations = destinations.filter((d) => d.verified);
  const selectedDest = verifiedDestinations.find((d) => d.destination_id === selectedDestId);
  const netReady = feePreview && feePreview.meets_minimum && feePreview.nets_positive;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="withdrawal-modal">
      <div className="w-full max-w-md rounded-xl border border-zinc-800/50 bg-zinc-900 p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wallet size={20} weight="duotone" className="text-emerald-500" />
            <h3 className="text-sm font-bold text-zinc-50" style={{ fontFamily: "Manrope" }}>Withdraw Funds</h3>
          </div>
          <button onClick={() => { setOpen(false); setStage("destination"); setAmount(""); }} data-testid="close-withdrawal-modal" className="text-zinc-500 hover:text-zinc-200">
            <X size={16} />
          </button>
        </div>

        {/* Mini step indicator */}
        <div className="mb-4 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
          <span className={`flex items-center gap-1.5 ${stage === "destination" ? "text-sky-400" : "text-emerald-400"}`}>
            <ShieldCheck size={12} weight="duotone" /> 1. Verify destination
          </span>
          <div className={`flex-1 h-px ${stage === "amount" ? "bg-emerald-500/60" : "bg-zinc-800"}`} />
          <span className={`flex items-center gap-1.5 ${stage === "amount" ? "text-sky-400" : "text-zinc-600"}`}>
            <Receipt size={12} weight="duotone" /> 2. Amount & Fees
          </span>
        </div>

        {/* ====== STAGE 1: DESTINATION ====== */}
        {stage === "destination" && (
          <>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-blue-400">Your Verified Destinations</p>
              {!addMode && (
                <button type="button" onClick={() => { setAddMode(true); setVerifyError(""); }} data-testid="add-destination-btn"
                  className="text-[10px] text-sky-400 hover:text-sky-300 font-medium">+ Add new</button>
              )}
            </div>

            {verifiedDestinations.length === 0 && !addMode && (
              <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-200" data-testid="no-destinations-warning">
                No verified withdrawal destination. Add one to receive funds.
              </div>
            )}
            {verifiedDestinations.length > 0 && !addMode && (
              <div className="space-y-1.5" data-testid="destinations-list">
                {verifiedDestinations.map((d) => (
                  <label key={d.destination_id} className={`flex items-start gap-2 p-2.5 rounded-md border cursor-pointer ${selectedDestId === d.destination_id ? "border-emerald-500/60 bg-emerald-500/5" : "border-zinc-800 bg-zinc-950"}`}>
                    <input type="radio" data-testid={`dest-radio-${d.destination_id}`} checked={selectedDestId === d.destination_id} onChange={() => setSelectedDestId(d.destination_id)} className="mt-0.5 accent-emerald-500" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11px] font-bold text-zinc-100">{d.type === "upi" ? "UPI" : "Bank"}</span>
                        <CheckCircle size={11} weight="fill" className="text-emerald-400" />
                        {d.is_primary && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30">Primary</span>}
                      </div>
                      <p className="text-[11px] text-zinc-400 font-mono mt-0.5 truncate">
                        {d.type === "upi" ? d.upi_id : `${d.bank_name} · ••••${(d.account_number || "").slice(-4)} · ${d.ifsc_code}`}
                      </p>
                      <p className="text-[10px] text-zinc-600 mt-0.5">Holder: {d.holder_name}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {/* Add new destination */}
            {addMode && (
              <div className="space-y-2 p-3 rounded-md border border-sky-500/30 bg-sky-500/5" data-testid="add-destination-form">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-bold text-sky-300">Add withdrawal destination</p>
                  <button type="button" onClick={() => setAddMode(false)} className="text-zinc-500 hover:text-zinc-200"><X size={12} /></button>
                </div>
                <p className="text-[10px] text-zinc-400 leading-relaxed">
                  Razorpay will verify the beneficiary name matches your registered name: <span className="text-sky-300 font-semibold">{registeredName || "—"}</span>
                </p>
                <div className="flex gap-2">
                  <label className="flex-1 flex items-center gap-2 p-2 rounded-md border border-zinc-800 cursor-pointer hover:border-zinc-700">
                    <input type="radio" data-testid="dest-type-bank" checked={destType === "bank"} onChange={() => setDestType("bank")} className="accent-sky-400" />
                    <span className="text-[11px] text-zinc-200">Bank account</span>
                  </label>
                  <label className="flex-1 flex items-center gap-2 p-2 rounded-md border border-zinc-800 cursor-pointer hover:border-zinc-700">
                    <input type="radio" data-testid="dest-type-upi" checked={destType === "upi"} onChange={() => setDestType("upi")} className="accent-sky-400" />
                    <span className="text-[11px] text-zinc-200">UPI</span>
                  </label>
                </div>

                {destType === "bank" ? (
                  <>
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Name</label>
                      <Input data-testid="dest-holder-name" placeholder="Account holder name (as per bank)" value={holderName} onChange={(e) => setHolderName(e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm mt-1" />
                    </div>
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Bank name</label>
                      <Input data-testid="dest-bank-name" placeholder="e.g. HDFC Bank" value={bankName} onChange={(e) => setBankName(e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm mt-1" />
                    </div>
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Account number</label>
                      <Input data-testid="dest-account-number" placeholder="Account number" value={accountNum} onChange={(e) => setAccountNum(e.target.value.replace(/\D/g, ""))} className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm mt-1 font-mono" />
                    </div>
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">IFSC code</label>
                      <Input data-testid="dest-ifsc" placeholder="e.g. HDFC0001234" value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm mt-1 font-mono" maxLength={11} />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Name</label>
                      <Input data-testid="dest-holder-name" placeholder="Account holder name" value={holderName} onChange={(e) => setHolderName(e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm mt-1" />
                    </div>
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">UPI ID</label>
                      <Input data-testid="dest-upi-input" placeholder="yourname@upi" value={newUpi} onChange={(e) => setNewUpi(e.target.value.trim())} className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm mt-1" />
                    </div>
                  </>
                )}
                {verifyError && (
                  <p className="text-[10px] text-rose-400" data-testid="dest-verify-error">{verifyError}</p>
                )}
                <button type="button" onClick={handleVerifyDestination} disabled={verifying} data-testid="verify-destination-btn"
                  className="w-full py-2 rounded-md text-[11px] font-bold bg-sky-600/20 border border-sky-500/40 text-sky-300 hover:bg-sky-600/30 disabled:opacity-40 flex items-center justify-center gap-1.5">
                  <Bank size={11} weight="bold" />
                  {verifying ? "Verifying with Razorpay…" : "Verify with Razorpay"}
                </button>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <button type="button" onClick={() => { setOpen(false); setStage("destination"); }} className="px-4 py-2.5 rounded-lg text-xs border border-zinc-800 text-zinc-400 hover:text-zinc-100">Cancel</button>
              <button type="button" disabled={!selectedDestId || addMode} onClick={() => setStage("amount")} data-testid="continue-to-amount-btn"
                className="flex-1 py-2.5 rounded-lg text-xs font-bold bg-sky-600 text-white hover:bg-sky-500 disabled:opacity-50 flex items-center justify-center gap-2">
                Continue to amount <ArrowUpRight size={12} weight="bold" />
              </button>
            </div>
          </>
        )}

        {/* ====== STAGE 2: AMOUNT + FEES ====== */}
        {stage === "amount" && (
          <form onSubmit={handleSubmit} className="space-y-3">
            {selectedDest && (
              <div className="p-2.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2" data-testid="selected-destination-banner">
                <CheckCircle size={14} weight="fill" className="text-emerald-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider">Verified destination</p>
                  <p className="text-[11px] text-zinc-200 truncate font-mono">
                    {selectedDest.type === "upi"
                      ? `UPI · ${selectedDest.upi_id}`
                      : `${selectedDest.bank_name} · ••••${(selectedDest.account_number || "").slice(-4)} · ${selectedDest.ifsc_code}`}
                  </p>
                </div>
                <button type="button" onClick={() => setStage("destination")} className="text-[10px] text-sky-400 hover:text-sky-300 font-bold">Change</button>
              </div>
            )}

            {/* PAN block — soft-threshold aware (Jun 2026):
                 • Under ₹20k/month  → PAN optional, TDS = 0%.
                 • Crossing ₹20k/month & PAN missing → hard block until user
                   either verifies PAN (2% TDS) or acknowledges 10% TDS.
                 • PAN verified → shown as verified. */}
            {(() => {
              const gate = feePreview?.pan_gate;
              const belowThreshold = feePreview && gate && !gate.crosses_threshold && !panState.verified;
              const needsGate = feePreview && gate && gate.required;
              // Verified state
              if (panState.verified) {
                return (
                  <div className="p-3 rounded-md border bg-sky-500/5 border-sky-500/30" data-testid="pan-status-card">
                    <div className="flex items-start gap-2">
                      <IdentificationCard size={16} weight="duotone" className="text-sky-400 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <p className="text-[11px] font-bold text-sky-300">PAN Verified</p>
                          <CheckCircle size={11} weight="fill" className="text-emerald-400" />
                        </div>
                        <p className="text-[11px] text-zinc-300 font-mono tracking-widest" data-testid="pan-masked">
                          {panState.pan_masked}
                        </p>
                        {panState.name_on_pan && (
                          <p className="text-[10px] text-zinc-500 mt-0.5">Name on PAN: {panState.name_on_pan}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setPanModalOpen(true)}
                        data-testid="change-pan-btn"
                        className="text-[10px] font-bold text-sky-400 hover:text-sky-300"
                      >
                        Change PAN
                      </button>
                    </div>
                  </div>
                );
              }
              // Hard block: crossing threshold, no PAN
              if (needsGate) {
                return (
                  <div className="p-3 rounded-md border bg-rose-500/10 border-rose-500/50" data-testid="pan-gate-required">
                    <div className="flex items-start gap-2 mb-2">
                      <IdentificationCard size={16} weight="duotone" className="text-rose-400 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-bold text-rose-300">PAN verification required</p>
                        <p className="text-[10px] text-rose-200/90 mt-0.5 leading-relaxed">
                          This withdrawal pushes your monthly total to{" "}
                          <span className="font-bold">
                            ₹{((gate.month_gross_before || 0) + parseFloat(amount || 0)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                          </span>{" "}
                          — exceeding the ₹{Number(gate.threshold_inr).toLocaleString("en-IN")} monthly limit.
                          Verify your PAN card to withdraw any amount. Without a verified PAN card, you can only withdraw within ₹20,000 every month.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPanModalOpen(true)}
                        data-testid="pan-gate-verify-btn"
                        className="px-4 py-1.5 rounded-md text-[11px] font-bold bg-sky-500 hover:bg-sky-400 text-black transition-colors"
                      >
                        Verify PAN Card
                      </button>
                    </div>
                  </div>
                );
              }
              // Below threshold, no PAN — mild info banner
              if (belowThreshold) {
                return (
                  <div className="p-3 rounded-md border bg-zinc-800/40 border-zinc-700/60" data-testid="pan-below-threshold">
                    <div className="flex items-start gap-2">
                      <IdentificationCard size={16} weight="duotone" className="text-zinc-400 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-bold text-zinc-200">PAN not required yet</p>
                        <p className="text-[10px] text-zinc-400 mt-0.5 leading-relaxed">
                          Monthly total after this withdrawal:{" "}
                          <span className="font-bold text-zinc-200">
                            ₹{((gate.month_gross_before || 0) + parseFloat(amount || 0)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                          </span>
                          {" "}/ ₹{Number(gate.threshold_inr).toLocaleString("en-IN")}. No TDS applies below this threshold.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPanModalOpen(true)}
                        data-testid="add-pan-btn"
                        className="text-[10px] font-bold text-sky-400 hover:text-sky-300 whitespace-nowrap"
                      >
                        Verify PAN
                      </button>
                    </div>
                  </div>
                );
              }
              // No preview yet, PAN missing — neutral prompt
              return (
                <div className="p-3 rounded-md border bg-zinc-800/40 border-zinc-700/60" data-testid="pan-status-card">
                  <div className="flex items-start gap-2">
                    <IdentificationCard size={16} weight="duotone" className="text-zinc-400 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold text-zinc-200">PAN optional under ₹20,000/month</p>
                      <p className="text-[10px] text-zinc-400 mt-0.5 leading-relaxed">
                        Verify your PAN in advance to keep TDS at 2% when your monthly withdrawals cross ₹20,000.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPanModalOpen(true)}
                      data-testid="add-pan-btn"
                      className="text-[10px] font-bold text-sky-400 hover:text-sky-300"
                    >
                      Verify PAN
                    </button>
                  </div>
                </div>
              );
            })()}

            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                Amount (Min ₹{(feePreview?.min_withdrawal || 1000).toLocaleString("en-IN")} · Max ₹{maxWithdraw.toLocaleString("en-IN")})
              </label>
              <Input data-testid="withdrawal-amount-input" type="number" placeholder="₹ Amount" value={amount} onChange={(e) => setAmount(e.target.value)} min={feePreview?.min_withdrawal || 1000} max={maxWithdraw}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 text-lg font-mono" />
              {feePreview?.monthly_limit?.used && (
                <p data-testid="monthly-limit-notice" className="text-[10px] text-rose-400 mt-1.5 font-bold">
                  You have already made your withdrawal for this month — only 1 withdrawal per month is allowed. Next window opens on the 1st of next month.
                </p>
              )}
              {feePreview?.payout && !feePreview.payout.instant && !feePreview?.monthly_limit?.used && parseFloat(amount) > 0 && (
                <p data-testid="credit-date-notice" className="text-[10px] text-amber-300 mt-1.5">
                  Amounts of ₹{(feePreview.payout.instant_max_inr || 7000).toLocaleString("en-IN")}+ are credited on the {feePreview.payout.credit_day_of_month}th of next month ({feePreview.payout.scheduled_credit_date}).
                </p>
              )}
              {breakdown?.is_capped && (
                <p data-testid="withdrawal-cap-notice" className="text-[10px] text-amber-300 mt-1.5">
                  Earnings capped at {breakdown.cap_pct}% ({(breakdown.cap_reason || "").replace(/_/g, " ")}).
                </p>
              )}
            </div>

            {/* Fee breakdown */}
            {feePreview && (
              <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 space-y-1.5" data-testid="fee-breakdown">
                <p className="text-[10px] font-bold uppercase tracking-wider text-blue-400 mb-1.5">Fee breakdown</p>
                <div className="flex justify-between text-[12px]">
                  <span className="text-zinc-400">Gross withdrawal</span>
                  <span className="text-zinc-100 font-mono" data-testid="fee-gross">₹{feePreview.gross_amount.toLocaleString("en-IN", {minimumFractionDigits: 2})}</span>
                </div>
                <div className="flex justify-between text-[12px]">
                  <span className="text-rose-300">– GST ({feePreview.gst_rate_pct}%)</span>
                  <span className="text-rose-300 font-mono" data-testid="fee-gst">– ₹{feePreview.gst_amount.toLocaleString("en-IN", {minimumFractionDigits: 2})}</span>
                </div>
                {feePreview.tds_applies && feePreview.tds_amount > 0 && (
                  <div className="flex justify-between text-[12px]" data-testid="fee-tds-row">
                    <span className="text-rose-300">– TDS ({feePreview.tds_rate_pct}%) · Sec 194H</span>
                    <span className="text-rose-300 font-mono" data-testid="fee-tds">– ₹{feePreview.tds_amount.toLocaleString("en-IN", {minimumFractionDigits: 2})}</span>
                  </div>
                )}
                {!feePreview.tds_applies && feePreview.month_gross_after > 0 && (
                  <p className="text-[9px] text-zinc-500 italic" data-testid="tds-threshold-note">
                    TDS 2% will apply once your monthly gross withdrawals cross ₹{feePreview.tds_threshold_inr.toLocaleString("en-IN")}.
                    This month so far: ₹{feePreview.month_gross_before.toLocaleString("en-IN")} → after this: ₹{feePreview.month_gross_after.toLocaleString("en-IN")}.
                  </p>
                )}
                <div className="flex justify-between text-[12px]">
                  <span className="text-rose-300">– Admin fee</span>
                  <span className="text-rose-300 font-mono" data-testid="fee-admin">– ₹{feePreview.admin_fee.toLocaleString("en-IN", {minimumFractionDigits: 2})}</span>
                </div>
                <div className="border-t border-zinc-800 pt-1.5 mt-1.5 flex justify-between text-[13px] font-bold">
                  <span className="text-emerald-300">You receive</span>
                  <span className={`font-mono ${netReady ? "text-emerald-300" : "text-rose-400"}`} data-testid="fee-net">
                    ₹{feePreview.net_payout.toLocaleString("en-IN", {minimumFractionDigits: 2})}
                  </span>
                </div>
                {!feePreview.meets_minimum && (
                  <p className="text-[10px] text-rose-400 mt-1">Minimum withdrawal is ₹{feePreview.min_withdrawal.toLocaleString("en-IN")}.</p>
                )}
                {feePreview.meets_minimum && !feePreview.nets_positive && (
                  <p className="text-[10px] text-rose-400 mt-1">Net payout is zero or negative — increase the amount.</p>
                )}
              </div>
            )}

            {/* Auto-approval status */}
            {feePreview?.auto_approval && netReady && (
              <div
                data-testid="auto-approval-card"
                className={`p-3 rounded-lg border ${feePreview.auto_approval.will_auto_approve
                  ? "bg-emerald-500/10 border-emerald-500/30"
                  : "bg-amber-500/10 border-amber-500/30"}`}
              >
                <div className="flex items-start gap-2">
                  {feePreview.auto_approval.will_auto_approve ? (
                    <CheckCircle size={16} weight="fill" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Receipt size={16} weight="duotone" className="text-amber-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`text-[11px] font-bold ${feePreview.auto_approval.will_auto_approve ? "text-emerald-300" : "text-amber-300"}`}>
                      {feePreview.auto_approval.will_auto_approve
                        ? (feePreview.payout?.instant
                          ? "Auto-approved · Funds within 1 hour"
                          : `Auto-approved · Credited on ${feePreview.payout?.scheduled_credit_date}`)
                        : `Admin review required · Credited on ${feePreview.payout?.scheduled_credit_date} after approval`}
                    </p>
                    <p className="text-[10px] text-zinc-400 mt-1 leading-relaxed">{feePreview.auto_approval.reason}</p>
                    {!feePreview.auto_approval.will_auto_approve && (
                      <p className="text-[10px] text-zinc-500 mt-1">
                        Trust progress: <span className="font-bold text-zinc-300">{feePreview.auto_approval.trust_progress}</span> successful withdrawals on distinct days.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <p className="text-[10px] text-zinc-600 text-center" data-testid="monthly-limit-note">
              Limit: 1 withdrawal per month · Amounts of ₹7,000+ are credited on the 5th of the next month.
            </p>

            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setStage("destination")} className="px-4 py-2.5 rounded-lg text-xs border border-zinc-800 text-zinc-400 hover:text-zinc-100">Back</button>
              <button
                type="submit"
                disabled={
                  submitting ||
                  !netReady ||
                  !selectedDestId ||
                  (parseFloat(amount) > maxWithdraw) ||
                  (feePreview?.monthly_limit?.used) ||
                  // If PAN is missing AND this withdrawal crosses the ₹20k threshold,
                  // the user MUST use one of the two CTAs in the PAN gate banner
                  // (Verify PAN, or explicitly acknowledge 10% TDS).
                  (feePreview?.pan_gate?.required)
                }
                data-testid="submit-withdrawal-btn"
                className="flex-1 py-2.5 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
              >
                {submitting
                  ? "Processing..."
                  : feePreview?.pan_gate?.required
                    ? "Choose PAN option above"
                    : `Withdraw ₹${(feePreview?.net_payout || 0).toLocaleString("en-IN")}`}
              </button>
            </div>
          </form>
        )}
      </div>

      <PanVerifyModal
        open={panModalOpen}
        onClose={() => setPanModalOpen(false)}
        initialPan={panState.verified ? "" : ""}
        onVerified={() => { setPanModalOpen(false); refreshState(); }}
      />
    </div>
  );
}
